import { motion } from "framer-motion";
import { TrendingUp, Users2, Globe2, Award } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   Hero metrics
   ───────────────────────────────────────────────────────────────────────── */
const metrics = [
  {
    value: "180M+",
    label: "Policies & Members Processed Annually",
    Icon: Users2,
  },
  {
    value: "30%+",
    label: "of India's Group Life Volume Managed",
    Icon: TrendingUp,
  },
  {
    value: "20+",
    label: "Insurers Across India & Southeast Asia",
    Icon: Globe2,
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Outcome cards — proof depth
   ───────────────────────────────────────────────────────────────────────── */
const outcomes = [
  {
    client: "Aditya Birla Sun Life Insurance",
    highlight: "60% reduction in group operations cost",
    description: "Modernized group core platform and operations.",
  },
  {
    client: "Aditya Birla Health Insurance",
    highlight: "₹300 Cr+ new business enabled",
    description: "Scaled Retail under Group (RUG) distribution.",
  },
  {
    client: "New India Assurance",
    highlight: "Accelerated digital distribution",
    description: "Enabled multi-channel policy issuance across lines.",
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Stylized client wordmarks — to be replaced with real logo files later
   ───────────────────────────────────────────────────────────────────────── */
const clients = [
  "LIC of India",
  "Aditya Birla Sun Life Insurance",
  "Aditya Birla Health Insurance",
  "Pramerica Life Insurance",
  "Deposit Insurance & Credit Guarantee Corporation",
  "Bank of Uganda",
  "DCB Bank",
  "Prestige Assurance Plc.",
  "PMSBY",
  "IndiaFirst Life Insurance",
  "Bank of India",
  "New India Assurance",
  "Tata AIA Life Insurance",
  "SBI Life Insurance",
  "Kotak Life Insurance",
  "HDFC Life Insurance",
  "HSBC Canara Life Insurance",
  "Bharti Axa Life Insurance",
];

export const Proof = () => {
  return (
    <section
      id="proof"
      data-testid="proof-section"
      style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)" }}
      className="border-y border-hairline"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 md:mb-14 max-w-3xl mx-auto"
        >
          <div className="mb-6">
            <span className="overline text-copper">Proof &amp; Metrics</span>
          </div>
          <h2
            data-testid="proof-title"
            className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.1] text-ink mb-5"
          >
            Proven at Scale.{" "}
            <span className="text-copper">Trusted by Leading Insurers.</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[800px] mx-auto">
            Gradatim powers high-volume, high-complexity insurance operations
            across leading insurers in India and Southeast Asia.
          </p>
        </motion.div>

        {/* METRICS ROW */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 md:mb-20">
          {metrics.map((m, i) => (
            <motion.div
              key={m.value}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="relative bg-cream border border-hairline rounded-2xl p-6 md:p-7 min-h-[140px] flex flex-col justify-center items-center text-center overflow-hidden group hover:border-copper/40 transition-colors"
              data-testid={`metric-card-${i}`}
            >
              <div aria-hidden className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-copper/5 blur-2xl" />
              <m.Icon className="h-5 w-5 text-copper mb-3 relative" strokeWidth={1.6} />
              <div className="font-display text-4xl md:text-[40px] tracking-tight text-ink leading-none mb-3 relative">
                {m.value}
              </div>
              <div className="text-[13px] md:text-sm text-slate-600 font-medium leading-snug relative max-w-[16rem]">
                {m.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CLIENT LOGOS STRIP */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20"
          data-testid="clients-strip"
        >
          <div className="text-center mb-6">
            <span className="text-[13px] md:text-sm text-slate-600 font-medium tracking-wide">
              Trusted by Industry Leaders
            </span>
          </div>

          {/* Logo marquee — wordmarks act as placeholders until real logo files are added.
              Replace each div below with <img src="/clients/<file>.png" ... /> when you upload logos. */}
          <div className="relative overflow-hidden border-y border-hairline py-7">
            <div className="flex gap-12 md:gap-16 animate-marquee w-max">
              {[...clients, ...clients].map((name, i) => (
                <div
                  key={`${name}-${i}`}
                  className="flex items-center gap-3 text-slate-700 hover:text-ink transition-colors whitespace-nowrap"
                  data-testid={`client-logo-${i}`}
                >
                  <span className="h-1.5 w-1.5 bg-copper rounded-full" />
                  <span className="font-display font-medium text-base md:text-lg tracking-tight">
                    {name}
                  </span>
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-20 md:w-28 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-20 md:w-28 bg-gradient-to-l from-white to-transparent" />
          </div>
        </motion.div>

        {/* OUTCOME CARDS */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <Award className="h-4 w-4 text-copper" strokeWidth={2} />
            <span className="overline text-slate-500">Business Impact</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map((o, i) => (
              <motion.article
                key={o.client}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
                className="bg-white border border-hairline rounded-2xl p-5 md:p-6 min-h-[160px] flex flex-col hover:border-copper/40 hover:-translate-y-0.5 transition-all duration-300"
                style={{ boxShadow: "0 4px 12px rgba(2,6,23,0.04)" }}
                data-testid={`outcome-card-${i}`}
              >
                <div className="text-[14px] font-bold text-slate-900 leading-snug mb-3">
                  {o.client}
                </div>
                <div className="font-display text-[18px] md:text-[20px] tracking-tight text-copper leading-snug mb-3 font-medium">
                  {o.highlight}
                </div>
                <p className="text-[13px] text-slate-600 leading-relaxed mt-auto">
                  {o.description}
                </p>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
